import { Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { formsCompletionRate } from '#lib/react-query/queries.ts';
import { STAT_WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from '../Base.tsx';
import {
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '../InfoTemplateComponents.tsx';
import { useFormWidget } from '../use-form-widget.ts';
import { StatBase } from './StatBase.tsx';

const widgetTitle = 'Forms Completion Rate';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Shows the&nbsp;
        <Typography component="span" variant="inherit" fontWeight="bold">
          percentage of assigned forms that were completed
        </Typography>
        &nbsp;during the selected date range. This metric helps evaluate patient
        engagement with forms and the effectiveness of form workflows.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Form Completion Rate =" indented>
        <Typography variant="body2" sx={{ pl: 5, mb: 2 }}>
          (Completed forms assigned during selected date range) ÷ (Number of
          forms assigned during the selected date range) × 100
        </Typography>
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Forms assigned within the selected date range',
            'Forms not deleted',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList items={['Deleted forms']} />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            'Assignments that occurred earlier but were completed in the range are excluded',
            'Forms completed after the selected date range, but assigned during, contribute to the completion rate',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

export function FormsCompletionRate() {
  const widget = useFormWidget();

  const { data, isLoading } = useQuery(
    formsCompletionRate(widget.getCommonRequestParams())
  );

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={STAT_WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <StatBase
      data={{
        id: 'forms-completion-rate',
        title: widgetTitle,
        value: data?.value ?? null,
        valueType: 'percentage',
        trend: data?.trend
          ? {
              ...data.trend,
              daysInPeriod: widget.homeLoaderData.totalDaysInRange,
            }
          : undefined,
      }}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    />
  );
}
